export type UsageSeverity = 'warning' | 'critical';

export type OperatorAlert = {
  kind: string;
  severity: UsageSeverity;
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  current: number;
  max: number;
  installedDevices?: number;
  activeDevices30d?: number;
  ratio?: number;
  message: string;
};

export const formatBytes = (bytes?: number | null) => {
  const n = Number(bytes ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value < 10 && i > 0 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
};

export const formatCount = (value?: number | null) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('hu-HU').format(Math.round(n));
};

export const formatUsageDate = (value?: string | null) => {
  if (!value) return '—';
  // DateOnly serializes as "yyyy-MM-dd"
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('hu-HU');
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('hu-HU');
};

export const deviceUtilizationPct = (
  current: number,
  max: number
): number | null => {
  if (!max || max <= 0) return null;
  return Math.min(999, Math.round((current / max) * 100));
};

export const severityColor = (
  severity?: string | null
): 'warning' | 'error' | 'primary' => {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'primary';
};

export const normalizeOperatorAlerts = (usage: any): OperatorAlert[] => {
  const raw =
    usage?.operatorAlerts ??
    usage?.OperatorAlerts ??
    usage?.nearDeviceLimit ??
    usage?.NearDeviceLimit ??
    [];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((row: any): OperatorAlert | null => {
      const max = Number(row.max ?? row.Max ?? row.maxDevices ?? row.MaxDevices ?? 0);
      const current = Number(
        row.current ??
          row.Current ??
          Math.max(
            Number(row.installedDevices ?? row.InstalledDevices ?? 0),
            Number(row.activeDevices30d ?? row.ActiveDevices30d ?? 0)
          )
      );
      const ratio =
        row.ratio ??
        row.Ratio ??
        (max > 0 ? current / max : 0);
      const severity: UsageSeverity =
        row.severity === 'critical' || row.Severity === 'critical' || ratio >= 1
          ? 'critical'
          : 'warning';

      const tenantId = row.tenantId ?? row.TenantId;
      if (!tenantId || max <= 0) return null;

      return {
        kind: row.kind ?? row.Kind ?? 'MaxDevices',
        severity,
        tenantId,
        tenantName: row.tenantName ?? row.TenantName,
        tenantSlug: row.tenantSlug ?? row.TenantSlug,
        current,
        max,
        installedDevices: Number(row.installedDevices ?? row.InstalledDevices ?? 0),
        activeDevices30d: Number(row.activeDevices30d ?? row.ActiveDevices30d ?? 0),
        ratio: Number(ratio),
        message:
          row.message ??
          row.Message ??
          (severity === 'critical'
            ? `Eszköz max túllépve: ${current} / ${max}`
            : `Eszköz max közelében: ${current} / ${max}`),
      };
    })
    .filter(Boolean) as OperatorAlert[];
};

export const CONTENT_INVENTORY_LABELS: { key: string; label: string }[] = [
  { key: 'articles', label: 'Cikkek' },
  { key: 'menuItems', label: 'Menüpontok' },
  { key: 'pages', label: 'Aloldalak' },
  { key: 'applets', label: 'Appletek' },
  { key: 'mapObjects', label: 'Térképelemek' },
  { key: 'officials', label: 'Vezetés' },
  { key: 'faqs', label: 'GYIK' },
  { key: 'events', label: 'Események' },
  { key: 'tags', label: 'Címkék' },
  { key: 'images', label: 'Képek' },
  { key: 'themes', label: 'Mobil témák' },
  { key: 'publishers', label: 'Publikálók' },
  { key: 'reports', label: 'Bejelentések' },
];

export const readContentInventory = (source: any) => {
  const inv =
    source?.contentInventory ??
    source?.ContentInventory ??
    source?.portfolio?.contentInventory ??
    source?.current?.contentInventory ??
    {};

  const rawLabels = source?.contentLabels ?? source?.ContentLabels;
  const labels = Array.isArray(rawLabels) && rawLabels.length > 0
    ? rawLabels.map((x: any) => ({
        key: x.key ?? x.Key,
        label: x.label ?? x.Label ?? x.labelHu ?? x.LabelHu,
      }))
    : CONTENT_INVENTORY_LABELS;

  return labels.map((item: { key: string; label: string }) => ({
    key: item.key,
    label: item.label,
    count: Number(inv?.[item.key] ?? 0),
  }));
};
